import { saveLang } from '@flexo/general';
import { useOnClickOutside } from '@flexo/hooks';
import i18next from 'i18next';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './floating-language-selector.module.scss';

const ENV = (import.meta as any).env;

export interface IFloatingLanguageSelector {
  /** Position from bottom of viewport (default: 24px) */
  bottomOffset?: number;
  /** Position from right of viewport (default: 24px) */
  rightOffset?: number;
  /** Override z-index (default: 1000) */
  zIndex?: number;
  /** Custom class for wrapper */
  className?: string;
}

export function FloatingLanguageSelector({
  bottomOffset = 48,
  rightOffset = 48,
  zIndex = 1000,
  className = '',
}: IFloatingLanguageSelector) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popup on outside click
  useOnClickOutside(
    containerRef,
    useCallback(() => setIsOpen(false), [])
  );

  // Get available languages from translations
  const availableLanguages = t('dropdowns.profile.languages', {
    returnObjects: true,
  }) as Record<string, string>;

  // Extract current language code (strip org prefix: "kwick_en" -> "en")
  const currentLanguageCode =
    i18next.language.split('_')[1]?.toUpperCase() || 'EN';

  const handleLanguageSelect = useCallback(
    (languageCode: string) => {
      const fullLanguageCode = `${ENV.VITE_APP_ORG}_${languageCode}`;
      i18n.changeLanguage(fullLanguageCode);
      saveLang(fullLanguageCode);
      setIsOpen(false);
    },
    [i18n]
  );

  const togglePopup = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    <div
      ref={containerRef}
      className={`${styles.FloatingLanguageSelector} ${className}`}
      style={{
        bottom: bottomOffset,
        right: rightOffset,
        zIndex,
      }}
    >
      {/* Trigger button */}
      <button
        type="button"
        className={styles.FloatingLanguageSelector__Trigger}
        onClick={togglePopup}
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span className={styles.FloatingLanguageSelector__Trigger__Label}>
          {currentLanguageCode}
        </span>
      </button>

      {/* Popup */}
      <div
        className={`${styles.FloatingLanguageSelector__Popup} ${
          isOpen ? styles['FloatingLanguageSelector__Popup--open'] : ''
        }`}
        role="menu"
      >
        {Object.keys(availableLanguages)
          .sort((a, b) =>
            availableLanguages[a].localeCompare(availableLanguages[b])
          )
          .map((languageCode) => {
            const isActive =
              languageCode === currentLanguageCode.toLowerCase();
            return (
              <div
                key={`lang-${languageCode}`}
                className={`${styles.FloatingLanguageSelector__Item} ${
                  isActive
                    ? styles['FloatingLanguageSelector__Item--active']
                    : ''
                }`}
                onClick={() => !isActive && handleLanguageSelect(languageCode)}
                role="menuitem"
                tabIndex={isActive ? -1 : 0}
              >
                <span className={styles.FloatingLanguageSelector__Item__Label}>
                  {availableLanguages[languageCode]}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default FloatingLanguageSelector;
